import type { RouteLocationRaw } from 'vue-router'
import { canOpenCredentialImport, credentialingIsOff, mayAccessCredentials } from '@/modules/users/credential/access'
import credentialImportReview from '@/assets/whats-new/ai-credential-import-review.webp'
import customizableSidebar from '@/assets/whats-new/customizable-sidebar.webp'

/**
 * The console's What's New announcements. Add an entry here to announce something; nothing else
 * needs to change. Newest first by convention, though display order comes from `publishedAt`.
 *
 * An entry is listed in Help > What's new from `publishedAt` on, for everyone who can use it. It
 * is also auto-shown -- the banner above the page, and the unread dot on the Help menu -- but only
 * to people for whom it is actually news: whose login predates it, and only until
 * `autoShowUntil`, so someone back from months away is not greeted with stale headlines.
 *
 * Dates are calendar dates (`YYYY-MM-DD`) in the viewer's own time zone.
 *
 * Announcing a plan feature: list it in `requiresFeatures`. Organizations without it see the
 * upsell version -- the same announcement with the plan pitch and an upgrade button -- but only
 * account owners, the only people who can act on it. Everyone else without it does not see the
 * item at all.
 *
 * Linking to something with no route of its own (a dialog, a wizard): give the page an
 * `?open=<intent>` handler with `useOpenIntent` and point `cta.route` at it. Keep `action` for
 * things that live in the page shell rather than on a page.
 */

/** Store getters, for rules the route guard does not already capture. */
export type WhatsNewGetters = any
export type WhatsNewRequirement = (_getters: WhatsNewGetters) => boolean

/** Things a call to action can do beyond navigating. Handled by the page shell (Full.vue). */
export type WhatsNewAction = 'customizeSidebar' | 'openSupportChat'

/**
 * A button on the item. Not offered to anyone it would fail for: an action unavailable here, a
 * named route they cannot open, or a `requires` that turns them away.
 */
export interface WhatsNewCta {
  label: string
  /**
   * Offers it in the upsell version too, under this label -- typically a preview rather than the
   * real thing. Without one, the upsell version leaves it out, and its upgrade button stands alone.
   */
  upsellLabel?: string
  /** One of route, href or action. */
  route?: RouteLocationRaw
  href?: string
  action?: WhatsNewAction
  /** Offered only to people this accepts -- whoever the destination would turn away. */
  requires?: WhatsNewRequirement
}

/**
 * A picture of the feature, at the top of its card in the panel. `src` and `poster` may be
 * imported assets or absolute URLs on a media host.
 */
export type WhatsNewMedia =
  | { kind: 'image', src: string, alt: string }
  /** A short muted loop (MP4 or WebM), in place of an animated GIF. The poster is its first frame. */
  | { kind: 'loop', src: string, poster: string, alt: string }
  /** A thumbnail that opens the video in a player. The poster defaults to YouTube's thumbnail. */
  | { kind: 'youtube', videoId: string, alt: string, poster?: string }

export interface WhatsNewItem {
  /** Stable and never reused: who has seen what is stored under it. */
  id: string
  /** When it appears, and the date shown beside it. */
  publishedAt: string
  title: string
  /** One sentence, for the banner. */
  summary: string
  /** Plain-text paragraphs for the What's New panel. */
  body: string[]
  /**
   * Auto-shown only to people whose Fareclock login was created before this date. Defaults to
   * `publishedAt`: someone who signed up after the change never knew it any other way.
   */
  autoShowToUsersCreatedBefore?: string
  /** No longer auto-shown from this date on. Still listed in the panel. */
  autoShowUntil: string
  /** Hidden from anyone without access to this route, by the router guard's own rule. */
  requiresRoute?: string
  /** Hidden from anyone this rejects. For permissions; plan features go in `requiresFeatures`. */
  requires?: WhatsNewRequirement
  /** Plan features the announced thing needs. Missing any makes this the upsell version. */
  requiresFeatures?: string[]
  /** Whose entry in `ROUTE_UPSELLING_DETAILS` supplies the upsell version's plan pitch. */
  upsellFeature?: string
  media?: WhatsNewMedia
  /**
   * The call to action, or alternatives in order of preference: the item offers the first one
   * this person can use. For when the feature needs a setup step first.
   */
  cta?: WhatsNewCta | WhatsNewCta[]
}

export const WHATS_NEW_ITEMS: WhatsNewItem[] = [
  {
    id: 'form-pdf-documents',
    publishedAt: '2026-11-05',
    title: 'Turn form entries into completed PDF documents',
    summary: 'Attach a PDF such as an I-9 or W-4 to a form, and each entry\'s answers are filled in on it and saved with the entry.',
    body: [
      'In the form builder\'s PDF document tab, upload a PDF and drag the form\'s fields onto its pages. Prefill boxes from each employee\'s profile, split answers into character boxes (3-2-4 for an SSN), and collect initials and signatures on the document.',
      'Mark boxes that hold ID numbers or other private details as sensitive to protect those answers. Open or download the completed PDF from each form entry. When you update the document, you choose whether past entries use the new version, and earlier copies are kept.'
    ],
    autoShowUntil: '2026-12-05',
    // Only for organizations with forms, and the admins who can build them.
    requiresRoute: 'forms-list',
    requiresFeatures: ['form_pdf'],
    upsellFeature: 'form_pdf',
    // Without the feature, admins can still upload and map a PDF in the builder; saving offers the upgrade.
    cta: { label: 'Open forms', upsellLabel: 'Try it in the form builder', route: { name: 'forms-list' } }
  },
  {
    id: 'form-manager-fields',
    publishedAt: '2026-11-04',
    title: 'Add fields to forms that only managers fill in',
    summary: 'Have managers review form entries and add their own answers, such as an approval or a follow-up note.',
    body: [
      'In a form\'s settings, under Manager fields, add fields that workers don\'t see or fill in. Managers complete them on the form entry after the worker submits it, for example to sign off, record an outcome or add notes.',
      'Choose which admins may edit each manager field, and whether it is shown in the mobile app.'
    ],
    autoShowUntil: '2026-12-04',
    requiresRoute: 'forms-list',
    requiresFeatures: ['form_manager'],
    upsellFeature: 'form_manager',
    cta: { label: 'Open forms', upsellLabel: 'Try it in the form builder', route: { name: 'forms-list' } }
  },
  {
    id: 'form-field-conditions',
    publishedAt: '2026-11-03',
    title: 'Show form fields only when they apply',
    summary: 'Show or hide a form field based on earlier answers, so workers only answer what applies to them.',
    body: [
      'In a field\'s settings in the form builder, add a visibility condition based on earlier answers, such as showing "Describe the injury" only when "Was anyone injured?" is Yes. Combine several conditions, requiring all or any of them to match.',
      'Hidden fields are skipped, so a long form stays short for most workers while still asking for details when they matter.'
    ],
    autoShowUntil: '2026-12-03',
    requiresRoute: 'forms-list',
    requiresFeatures: ['form_condition'],
    upsellFeature: 'form_condition',
    cta: { label: 'Open forms', upsellLabel: 'Try it in the form builder', route: { name: 'forms-list' } }
  },
  {
    id: 'month13-pay-computation',
    publishedAt: '2026-11-02',
    title: 'Choose what counts toward 13th month pay, and see how it was computed',
    summary: 'Set what 13th month pay includes and when it\'s paid, and see each worker\'s pay basis, tax and net amount on the pay run.',
    body: [
      'In Net Pay Settings, choose whether overtime, differential, absences, and late and undertime count toward 13th month pay. You can also set whether it\'s paid once or twice a year, when its coverage starts, and how pay periods that cross the coverage are counted.',
      'On a 13th month pay run, the pay summary now shows each worker\'s pay basis, gross 13th month pay, non-taxable and taxable amounts, deductions and net pay. Tax already withheld on bonuses over the non-tax exemption ceiling is credited back, and the credit is listed on the pay stub.',
      'The Net Pay 13th Month report now uses the same calculation, by year, and adds each worker\'s tax, net pay and remaining payable, with projected amounts in red. It lists salaried workers only for now, so hourly workers no longer appear. Reports saved with a date range open on the current year, so pick the year again.'
    ],
    autoShowUntil: '2026-12-02',
    // Only for organizations with net pay, and the admins who can change these settings.
    requiresRoute: 'net-pay-settings',
    cta: { label: 'Open Net Pay Settings', route: { name: 'net-pay-settings' } }
  },
  {
    id: 'intercom-support',
    publishedAt: '2026-10-15',
    title: 'Get help without leaving the console',
    summary: 'Message our support team, search help articles and find your past conversations, all from the chat button in the header.',
    body: [
      'We have moved our support to a new system. Click the chat button at the top of the console to message our team from whatever page you are on, or to search our help articles for step-by-step answers.',
      'When we reply, the chat button shows it, so you do not have to watch your email. Your conversations stay under Messages, and you are signed in automatically, so we already know who you are and which account you are asking about.'
    ],
    autoShowUntil: '2026-11-15',
    cta: { label: 'Open chat', action: 'openSupportChat' }
  },
  {
    id: 'schedule-controls',
    publishedAt: '2026-10-11',
    title: 'Keep schedules on budget, and lock them once approved',
    summary: 'Set hours budgets for each location or group, see scheduled hours against them as you build the schedule, and approve schedules so they stay put.',
    body: [
      'Set daily and weekly hours budgets for your whole company, or for each org unit, department or custom field group. While you build the schedule, the totals row shows scheduled paid hours against budget and turns red when a day or week goes over.',
      'Managers with the new "Approve schedule" permission review each group\'s hours on the Schedule Review page and approve the period. Once approved, other managers can\'t change it, or, if you prefer, can only make changes that don\'t add hours. The Labor Budget report compares budget, scheduled and actual hours.'
    ],
    autoShowUntil: '2026-11-11',
    // Only for organizations that schedule; the review and settings pages are where it starts.
    requiresRoute: 'schedules',
    requiresFeatures: ['schedule_controls'],
    upsellFeature: 'schedule_controls',
    cta: [
      {
        // The review page shows sample data in the upsell version, and needs budgets or approval turned
        // on otherwise.
        label: 'Open schedule review',
        upsellLabel: 'See how it works',
        route: { name: 'schedule-review' },
        requires: getters => getters.canManageSchedule && (!getters.hasScheduleControlsFeature || getters.scheduleBudgetsInUse)
      },
      {
        label: 'Set up schedule controls',
        route: { name: 'shift-defaults' }
      }
    ]
  },
  {
    id: 'org-week-start',
    publishedAt: '2026-10-11',
    title: 'Choose the day your week starts',
    summary: 'Set which day your organization\'s week starts on, and weekly reports, date pickers and the schedule follow it.',
    body: [
      'Weeks used to always start on Sunday. Now your organization can start them on any day, set by an admin with access to Organization Settings › Organization. The "This Week" and "Last Week" date ranges, the Week column and grouping in reports, scheduled reports, calendars in date pickers and the schedule all follow it.',
      'Overtime weeks are still set on each pay class, so changing this does not change pay. Admins who picked a first day of the week in the schedule settings keep their choice.'
    ],
    autoShowUntil: '2026-11-11',
    // Everyone sees it, since the setting changes their reports; only admins who can open the
    // settings page get the button.
    cta: { label: 'Set week start', route: { name: 'org-defaults' } }
  },
  {
    id: 'ai-credential-import',
    publishedAt: '2026-10-07',
    title: 'Import credentials in bulk with AI',
    summary: 'Upload a stack of licenses and certificates, and AI fills in who each one belongs to and when it expires.',
    body: [
      'Drop in PDFs, photos or a zip of scanned credentials. AI reads each page, matches it to the right person, and picks out the credential type, ID number, issue date and expiry date.',
      'Nothing is saved until you have reviewed it: check each match, fix anything it got wrong, and import them all in one go, with the original page attached to each credential.'
    ],
    autoShowUntil: '2026-11-07',
    requiresRoute: 'employee-list',
    requires: mayAccessCredentials,
    requiresFeatures: ['credential', 'document', 'credential_ai'],
    upsellFeature: 'credential_ai',
    media: {
      kind: 'image',
      src: credentialImportReview,
      alt: 'Reviewing five imported credential files, each matched to a worker and ready to save'
    },
    cta: [
      {
        label: 'Import credentials',
        upsellLabel: 'See how it works',
        route: { name: 'employee-list', query: { open: 'credential-import' } },
        requires: canOpenCredentialImport
      },
      {
        // The importer stays out of reach until someone turns credentialing on.
        label: 'Turn on credentialing',
        route: { name: 'employee-defaults' },
        requires: credentialingIsOff
      }
    ]
  },
  {
    id: 'customizable-sidebar',
    publishedAt: '2026-10-01',
    title: 'Make the sidebar your own',
    summary: 'Pin the pages you use most, group them into sections, and rename or recolor them.',
    body: [
      'Each admin now has their own sidebar. Add any page from the same list the header search uses, drag items to reorder them, and group them into sections you can collapse.',
      'Your layout is saved for you in this organization, so other admins keep theirs. Click "Customize sidebar" below the last item to get started.'
    ],
    autoShowUntil: '2026-11-01',
    media: {
      kind: 'image',
      src: customizableSidebar,
      alt: 'Customizing the sidebar: sections of pages on the left, and an item being given a new name, color and icon'
    },
    cta: { label: 'Customize sidebar', action: 'customizeSidebar' }
  }
]
